import { useServices } from '@/app/providers';
import { Button, Card } from '@/core/ui/components';
import { describeEvent, formatEventType } from '@/modules/audit/ui/components/AuditLogTable';
import type { AuditLogEntry } from '@yawi/types';
import { Activity, Clock, RefreshCw, User } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

interface TenantActivityLogProps {
  tenantId: string;
}

/**
 * Acciones de plataforma sobre este tenant (`admin/audit?tenantId=`). Lo que
 * hacen sus usuarios dentro del tenant vive en su propia bitácora (`GET /audit`).
 */
export function TenantActivityLog({ tenantId }: TenantActivityLogProps) {
  const { auditRepository } = useServices();

  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await auditRepository.list({ tenantId }, 1, 20);
      setLogs(result.data);
    } catch (err) {
      setLogs([]);
      setError(err instanceof Error ? err.message : 'Failed to load activity');
    } finally {
      setIsLoading(false);
    }
  }, [auditRepository, tenantId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const diff = Date.now() - date.getTime();

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90 flex items-center gap-2">
          <Activity className="w-5 h-5" />
          Activity Log
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchLogs}
          leftIcon={<RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />}
        >
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
        </div>
      ) : error ? (
        <div className="text-center py-6">
          <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
          <Button className="mt-3" variant="outline" size="sm" onClick={fetchLogs}>
            Retry
          </Button>
        </div>
      ) : logs.length === 0 ? (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">
          No activity recorded yet
        </p>
      ) : (
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {logs.map((log) => {
            const { title, subtitle } = describeEvent(log);
            return (
              <div
                key={log.id}
                className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50"
              >
                <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {formatEventType(log.eventType)}
                    {title && <span className="font-normal text-gray-500 dark:text-gray-400"> · {title}</span>}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    by {log.userEmail || log.userId || 'System'}
                  </p>
                  {subtitle && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate font-mono">
                      {subtitle}
                      {typeof log.details?.statusCode === 'number' && ` -> ${log.details.statusCode}`}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 shrink-0">
                  <Clock className="w-3 h-3" />
                  {formatTime(log.createdAt)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
